<template>
  <div
    ref="rootElement"
    :class="[
      'navbar-feed',
      { 'navbar-feed--variant-success': allFeedsUp },
      { 'navbar-feed--variant-danger': someFeedsDown },
    ]"
    @click="toggleDropdown"
  >
    <div class="navbar-feed__button">
      <NavbarFeedIcon
        :variant="feedIconVariant"
      />
      {{ buttonLabel }}
    </div>
    <div
      v-if="isDropdownToggled"
      class="navbar-feed__dropdown"
    >
      <div
        v-for="feed in allFeeds"
        :key="feed.feed"
        class="navbar-feed__dropdown-item"
      >
        <NavbarFeedIcon
          :variant="getFeedIconVariant(feed)"
        />
        {{ feedName(feed.feed) }}
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { filter, capitalize } from 'lodash';
import { onClickOutside } from '@vueuse/core';
import NavbarFeedIcon from './NavbarFeedIcon';

export default {
  components: {
    NavbarFeedIcon,
  },
  setup() {
    const store = useStore();

    const rootElement = ref(null);
    const isDropdownToggled = ref(false);
    const toggleDropdown = () => {
      isDropdownToggled.value = !isDropdownToggled.value;
    };

    const allFeeds = computed(() => store.getters.allFeeds);
    const allDownFeeds = computed(() => filter(allFeeds.value, (feed) => feed.status !== 'UP'));
    const allFeedsUp = computed(() => allDownFeeds.value.length === 0);
    const someFeedsDown = computed(() => allDownFeeds.value.length !== 0);
    const buttonIcon = computed(() => {
      if (someFeedsDown.value) return 'exclamation';
      return 'check';
    });
    const buttonLabel = computed(() => {
      if (allDownFeeds.value.length === 0) return 'Feeds healthy';
      if (allDownFeeds.value.length !== 1) return 'Feeds unhealthy';
      const firstDownFeed = allDownFeeds.value[0].feed;
      return `${firstDownFeed} down`;
    });
    const feedIconVariant = computed(() => {
      if (allFeedsUp.value) return 'success';
      return 'danger';
    });
    const getFeedIconVariant = ({ status }) => {
      if (status === 'UP') return 'success';
      return 'danger';
    };

    const feedName = (name) => {
      if (name === 'statsperform_betgenius') return 'Opta BG';
      if (name === 'statsperform_betradar') return 'Opta BR';
      if (name === 'statsperform_runningball') return 'Opta RB';
      if (name === 'statsperform') return 'Opta';
      if (name === 'odds_composer') return 'Odds Composer';
      if (name === 'runningball') return 'Running Ball';
      return capitalize(name);
    };

    onMounted(() => {
      store.dispatch('getAllFeeds');
    });
    onClickOutside(rootElement, () => {
      isDropdownToggled.value = false;
    });

    return {
      rootElement,
      isDropdownToggled,
      toggleDropdown,
      allFeeds,
      allFeedsUp,
      someFeedsDown,
      buttonIcon,
      buttonLabel,
      feedIconVariant,
      getFeedIconVariant,
      feedName,
    };
  },
};
</script>

<style lang="scss">
.navbar-feed {
  $block: &;
  position: relative;
  user-select: none;

  &__button {
    display: flex;
    height: 32px;
    padding: 4px 12px;
    justify-content: center;
    align-items: center;
    gap: 4px;
    border-radius: 4px;
    color: var(--neutral-text-default-resting, #191414);
    font-family: Rubik;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
  }

  &__dropdown {
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    min-width: 100%;
    margin-top: 4px;
    background-color: $white;
    color: $black;
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.15);
    border-radius: 4px;
    border: 1px solid rgba(221, 221, 221, 1);
    z-index: $dropdownMenuZIndex;

    &-item {
      display: inline-flex;
      align-items: center;
      white-space: nowrap;
      min-width: 100%;
      height: 44px;
      padding: 0 12px;
      gap: 4px;
    }
  }

  &--variant-success {
    #{$block}__button {
      background: var(--success-bg-weak-resting, #E5F8EE);
    }
  }

  &--variant-danger {
    #{$block}__button {
      background: var(--danger-bg-weak-resting, #FEE);
    }
  }
}
</style>
